<template>
	<div class="container" id="UserDashboard">
		<div v-if="page_loaded == 0" class="text-center mt-5 gold">
			<i class="fas fa-spinner fa-spin fa-2x"></i>
			<p>Loading...</p>
		</div>
		<div v-else>
			<div class="text-center gold mt-4">
				<h4>
					<i :class="page_icon"></i> <span class="ml-1" v-text="page_title"></span>
					<small v-if="more_members == 1 && admin_view == 1">
						<br />
						<router-link
							v-if="this.$store.state.profile.level >= 90"
							:to="'/members/'"
							class="gold small"
						>
							<i class="fas fa-users"></i> select member dashboard
						</router-link>
						<router-link v-else :to="'/team/'" class="gold small">
							<i class="fas fa-users"></i> team members list
						</router-link>
					</small>
				</h4>
			</div>

			<div v-if="canUserView === false">
				<b-alert show variant="danger" class="text-center">
					<h4>You don't have permission to view this...</h4>
				</b-alert>
			</div>
			<div v-else>

        <Shortcuts v-if="$route.meta.showShortcuts"></Shortcuts>

				<div v-for="section in dashboard" :key="section.slug" :id="section.slug">
					<div v-if="section.cards && section.cards.length > 0">
						<div class="mb-4">
							<h4
								class="
									text-muted
									border-top border-bottom
									p-2
									d-flex
									justify-content-between
									align-items-center
								"
							>
								<span>
									<i :class="section.icon" class="mr-2"></i>
									<span v-text="section.title"></span>
									<small v-if="section.slug != 'charts'" class="ml-2">
										<router-link
											:to="'/forum/categories/' + section.slug"
											class="gold small"
										>
											<i class="far fa-comments"></i> forum
										</router-link>
									</small>
								</span>

								<div v-if="section.slug === 'charts'">
									<router-link :to="'/profile_report/' + view_user_id" class="gold small">
										<i class="fas fa-chart-pie"></i> Statistic report
									</router-link>
								</div>
							</h4>

							<b-card-group columns :id="'section_' + section.slug">
								<b-card v-for="card in section.cards" :key="card.slug">
									<template #header>
										<div class="row">
											<div class="col text-left small">
												<span
													v-if="card.tooltip"
													class="float-left mr-1 gold"
													v-b-tooltip.hover
													:title="card.tooltip"
												>
													<i class="far fa-question-circle"></i>
												</span>
												<div v-if="card.card_type === 'forum'" class="">
													<span v-text="card.title"></span>
												</div>
												<div v-else v-text="card.title"></div>
											</div>
											<div v-if="card.refresh" class="col-4 text-right small">
												<b-button
													variant="link"
													class="btn-sm gold"
													@click="refreshData(card)"
													v-b-tooltip.hover
													:title="'Refresh ' + card.title + ' data'"
												>
													<i class="fas fa-sync"></i>
												</b-button>
											</div>
											<div v-else-if="card.access" class="col-4 text-right small">
												<small v-text="card.access.date"></small>
											</div>
										</div>
									</template>

									<div v-if="card.component && typeof card.component !== 'undefined'">
										<div v-if="card.overview_text !== 'undefined'" class="mt-2">
											<ul
												v-for="(item, index) in card.overview_text"
												:key="index"
												class="mb-0 pl-4"
											>
												<li v-html="item" class="small"></li>
											</ul>
										</div>

										<div v-if="card.component === 'ChartLine'" class="text-center">
											<div
												v-if="!card.hasChartData"
												class="col-12 p-2 text-center silver"
											>
												<p><i class="fas fa-chart-pie fa-2x"></i></p>
												<p><small>Nothing to show for the last 7 days...</small></p>
											</div>
											<div v-else>
												<chart-line :chartdata="card.chartdata" />
											</div>
										</div>

										<div
											v-if="card.component === 'BioTestingDashboardCard'"
											class="text-center"
										>
											<bio-testing-dashboard-card
												:userid="view_user_id"
												:params="card"
											/>
										</div>
										<div
											v-if="card.component === 'BioTestingAdminCard'"
											class="text-center"
										>
											<bio-testing-admin-card />
										</div>

										<div v-if="card.component === 'CountdownTimer'" class="text-center">
											<countdown-timer />
										</div>

										<div v-if="card.component === 'HabitsCard'" class="">
											<habits-card />
										</div>

										<div v-if="card.component === 'CourseCard'" class="">
											<course-card :data="card" />
										</div>

										<div
											v-if="card.component === 'MeditationManualLog'"
											class="text-center"
										>
											<meditation-manual-log />
										</div>

										<div v-if="card.component === 'DreamsLog'" class="text-center">
											<dreams-log :userid="view_user_id" :readonly="readonly" />
										</div>

										<div v-if="card.component === 'FeelingsLog'" class="text-center">
											<feelings-log :userid="view_user_id" :readonly="readonly" />
										</div>

										<div v-if="card.component === 'SharedUserData'" class="text-center">
											<shared-user-data />
										</div>

										<div v-if="card.component === 'ChallengesCard'" class="text-center">
											<challenges-card />
										</div>

										<div v-if="card.component === 'ForumItem'" class="text-left">
											<div
												v-if="
													(card.youtube && card.youtube.length > 0) ||
													(card.vimeo && card.vimeo.length > 0)
												"
												class="mb-2"
											>
												<video-embeds :embeddata="card" classW="" show_title="0" />
											</div>

											<div v-if="card.main_text !== null" class="">
												<p class="card-text" v-html="card.main_text"></p>
												<p
													@click="onClick($event, card, 'description')"
													class="text-right pointer gold small"
												>
													<i class="fas fa-minus"></i> less
												</p>
											</div>
											<div v-else-if="card.description" class="">
												<p class="card-text" v-html="card.description"></p>
												<p
													@click="onClick($event, card, 'main_text')"
													class="text-right pointer gold small"
												>
													<i class="fas fa-plus"></i> more
												</p>
											</div>

											<div v-if="card.list && card.list.items" class="card-body">
												<ul
													v-for="item in card.list.items"
													:key="'L_' + item.date"
													class="list-unstyled mb-0"
												>
													<dl class="row small">
														<dt class="col-7"><small v-text="item.date"></small></dt>
														<dd class="col-3">
															<span v-text="item.mins"></span><small>min</small>
														</dd>
													</dl>
												</ul>
											</div>
										</div>
									</div>

									<template #footer>
										<div
											v-if="card.can_comment && card.can_comment === 1"
											class=""
											:id="'reply_' + card.id"
										>
											<div class="row">
												<div class="col text-left">
													<small
														v-if="!card.reply_open || card.reply_open === 0"
														class="col gold pointer text-left"
														@click="onClick($event, card, 'replys')"
													>
														<i class="far fa-comments pr-1"></i>
														<span v-text="card.replies_count"></span> comments
													</small>
													<small
														v-else
														class="col gold pointer text-left"
														@click="onClick($event, card, 'close_comment')"
													>
														<i class="far fa-times-circle"></i> close comments
													</small>
												</div>

												<div v-if="card.link" class="col text-right">
													<a
														:href="card.link"
														class="btn-link small gold"
														v-text="card.link_text"
													></a>
												</div>
												<div v-else-if="card.user_name" class="col text-right">
													<small class="text-muted" v-text="card.user_name"></small>
												</div>
											</div>
										</div>
										<div v-else class="">
											<div class="row">
												<div class="col text-left">
													<small class="text-muted mr-2"></small>
												</div>

												<div v-if="card.link" class="col text-right">
													<a
														:href="card.link"
														class="btn-link small gold"
														v-text="card.link_text"
													></a>
												</div>
												<div v-else-if="card.user_name" class="col text-right">
													<small class="text-muted" v-text="card.user_name"></small>
												</div>
											</div>
										</div>
									</template>
								</b-card>
							</b-card-group>
						</div>
					</div>
				</div>
			</div>
		</div>

		<b-modal id="comments_wrapper" title="Comments / Reply" size="xl">
			<div v-if="active_card.id">
				<div v-if="active_card.replies !== null" class="text-left mb-3">
					<ul class="list-group" v-for="R in active_card.replies" :key="'R_' + R.id">
						<li class="list-group-item">
							<span v-text="R.title"></span> <span v-html="R.main_text"></span><br />
							<div
								v-if="
									(R.youtube && R.youtube.length > 0) || (R.vimeo && R.vimeo.length > 0)
								"
								class="col-md-4 mb-2"
							>
								<video-embeds :embeddata="R" classW="" show_title="0" />
							</div>

							<div class="text-right mt-1 small">
								<b v-text="R.user_name"></b>:
								<span v-text="R.access.date + ' ' + R.access.time"></span>
							</div>
						</li>
					</ul>
				</div>
				<div v-else class="text-muted">It's a ghost town in here... Start a conversation?</div>

				<div v-if="active_card.can_comment == 1">
					<p class="p-0 m-0 mt-3"><b>Post Reply</b></p>
					<b-form-textarea
						v-model="active_card.reply_text"
						placeholder="Enter something..."
						rows="3"
						max-rows="6"
						class="mt-2"
						@keyup="checkReplyForm"
					></b-form-textarea>

					<div class="text-right">
						<h5 v-if="error_message != ''" v-text="error_message" class="text-danger"></h5>
					</div>
				</div>
			</div>

			<template #modal-footer="{ cancel }">
				<div v-if="active_card.replys_loaded === 1">
					<button
						v-if="active_card.can_comment == 1"
						type="button"
						class="btn btn-primary"
						@click="replyAdd()"
						:disabled="!canSubmitReply"
					>
						Submit <i class="far fa-comment-dots"></i>
					</button>
					<small v-else class="text-muted mr-2"
						>Comments have been disabled for this post</small
					>

					<b-button size="sm" variant="danger" @click="cancel()"> Cancel </b-button>
				</div>
				<div v-else>Loading data...</div>
			</template>
		</b-modal>

		<b-modal
		class="modal-xl" ref="tour" id="tour" title="Website Tour" size="xl" ok-only ok-title="Close" 
		@hidden="tourClosed"
		>
			<div class="embed-responsive embed-responsive-16by9">
				<iframe width="560" height="315" src="https://www.youtube.com/embed/ZqUaBS8_bgE" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
			</div>
		</b-modal>
	</div>
</template>

<script>
import axios from 'axios';
import BioTestingAdminCard from '../components/BioTestingAdminCard.vue';
import BioTestingDashboardCard from '../components/BioTestingDashboardCard.vue';
import ChartLine from '../components/ChartLine.vue';
import CountdownTimer from '../components/CountdownTimer.vue';
import CourseCard from '../components/CourseCard.vue';
import DreamsLog from '../components/DreamsLog.vue';
import FeelingsLog from '../components/FeelingsLog.vue';
import HabitsCard from '../components/HabitsCard.vue';
import MeditationManualLog from '../components/MeditationManualLog.vue';
import SharedUserData from '../components/SharedUserData.vue';
import VideoEmbeds from '../components/VideoEmbeds.vue';
import ChallengesCard from '../components/ChallengesCard.vue';
import Shortcuts from "@/components/Dashboard/Shortcuts";

export default {
	name: 'UserDashboard',
	components: {
    Shortcuts,
		BioTestingAdminCard,
		BioTestingDashboardCard,
		ChartLine,
		CountdownTimer,
		CourseCard,
		DreamsLog,
		FeelingsLog,
		HabitsCard,
		MeditationManualLog,
		SharedUserData,
		VideoEmbeds,
		ChallengesCard
	},
	data() {
		return {
			active_card: {},
			canSubmitReply: false,
			canUserView: false,
			dashboard: {},
			error_message: '',
			more_members: 0,
			page_loaded: 0,
			page_title: 'Dashboard',
			page_icon: 'fas fa-home',
			profile: {},
			readonly: 1,
			sample_id: 1586913630,
			sample_data: 0,
			view_user_id: 0,
		};
	},
	computed: {
		admin_view() {
			var is_admin = 0;
			if (this.$store.state.profile.level >= 50) {
				is_admin = 1;
			}
			return is_admin;
		},
		user_level() {
			return this.$store.getters.profileGetUserlevel;
		},
	},
	methods: {
		checkReplyForm() {
			if (this.active_card.reply_text === '') {
				this.canSubmitReply = false;
			} else {
				this.canSubmitReply = true;
			}
		},

		getCardDetails(card) {
			let _this = this;
			let params = new URLSearchParams();
			params.append('token', this.$store.state.session_id);

			if (card.forum_id) {
				params.append('id', card.forum_id);
			} else {
				params.append('id', card.id);
			}
			params.append('type', card.type);

			this.active_card = {};

			axios({
				url: _this.$store.state.api_url + 'CardsGetDetails',
				params: params,
			})
				.then(function (response) {
					_this.active_card = card;

					_this.active_card.reply_text = '';
					_this.active_card.replys_loaded = 0;

					if (typeof card.type === 'undefined' || card.type === null) {
						console.log('card.type not set');
					} else if (card.type === 'single') {
						_this.active_card.main_text = response.data.post[card.id].main_text;
						_this.active_card.card_class = 'card_hightlight';
					} else if (card.type === 'replys') {
						if (response.data.replys !== null) {
							_this.active_card.replies = Object.values(response.data.replys);
						}
						_this.active_card.replys_loaded = 1;
					}
				})
				.catch(function (error) {
					console.log(error);
				});
		},

		getUserDashboardCards() {
			console.log('getUserDashboardCards:');

			let _this = this;

			// whos looking
			this.view_user_id = 0;
			this.sample_data = 0;

			this.canUserView = false;

			if (this.$route.params.user_id) {
				this.view_user_id = parseInt(this.$route.params.user_id);
			}

			if (this.$route.name == 'sample_data') {
				this.sample_data = this.sample_id;
				this.view_user_id = this.sample_id;
			}

			this.readonly = 0;

			let params = new URLSearchParams();
			params.append('token', this.$store.state.session_id);

			if (this.view_user_id > 0) {
				params.append('user_id', this.view_user_id);
				this.readonly = 1;
			}
			if (this.sample_data > 0) {
				params.append('sample_data', this.sample_data);
			}
			if (this.$route.meta.dash_type && this.$route.meta.dash_type == 'category') {
				params.append('category', this.$route.meta.category);
			}

			this.dashboard = {};

			axios({
				url: _this.$store.state.api_url + 'UserDashboardCardsGet',
				params: params,
			})
				.then(function (response) {
					if (response.data.status === 1) {
						_this.canUserView = true;
						if (response.data.dashboard !== null) {
							let entries = response.data.dashboard;
							_this.dashboard = entries;
							_this.page_title = _this.$route.meta.title;
							_this.more_members = 0;
						}

						if (response.data.profile !== null) {
							_this.profile = response.data.profile;
							_this.page_title = _this.profile.user_name + ' Dashboard';
							_this.more_members = 1;
						}

						if (response.data.page) {
							_this.page_title = response.data.page.title;
							_this.page_icon = response.data.page.icon;
						}

						_this.view_user_id = response.data.dashboard_profile_id;
					}

					_this.page_loaded = 1;
					_this.isUserNew();
				})
				.catch(function (error) {
					console.log(error);
				});
		},

		onClick(e, card, type) {
			e.preventDefault();
			console.log(type);
			if (type == 'replys') {
				card.type = 'replys';
				this.getCardDetails(card);
				this.$bvModal.show('comments_wrapper');
			} else if (type == 'main_text') {
				card.type = 'single';
				this.getCardDetails(card);
			} else if (type == 'description') {
				card.main_text = null;
				card.card_class = 'forum';
			} else if (type == 'post_reply') {
				card.reply_textarea_show = 1;
				card.reply_text = '';
			} else if (type == 'close_comment') {
				card.reply_textarea_show = 0;
				card.reply_open = 0;
				card.replies = null;
			}
		},

		refreshData(item) {
			console.log('refreshData');
			this.page_loaded = 0;
			let _this = this;
			let params = new URLSearchParams();
			params.append('token', this.$store.state.session_id);

			axios({
				url: _this.$store.state.api_url + item.refresh,
				params: params,
			})
				.then(function (response) {
					if (response.data.status == 1) {
						location.reload();
					} else {
						console.log(response.data);
					}
				})
				.catch(function (error) {
					console.log('Error:refreshData:' + error);
				});
		},

		replyAdd() {
			let _this = this;

			console.log('replyAdd');

			if (this.active_card.reply_text === null || this.active_card.reply_text === '') {
				this.error_message = 'You need to add some content to the Post Reply field!';
				this.canSubmitReply = false;
			} else {
				this.error_message = '';
				this.canSubmitReply = true;
			}

			if (this.canSubmitReply === true) {
				let params = new URLSearchParams();
				params.append('token', _this.$store.state.session_id);

				if (_this.active_card.forum_id) {
					params.append('post_id', _this.active_card.forum_id);
				} else {
					params.append('post_id', _this.active_card.id);
				}

				/*params.append('reply_id', this.reply_id);*/

				params.append('title', '');
				params.append('main_text', _this.active_card.reply_text);

				axios({
					method: 'post',
					url: _this.$store.state.api_url + 'forum/replyAdd',
					data: params,
				})
					.then(function (response) {
						if (response.data.status == 1) {
							if (typeof response.data.added_data_id !== 'undefined') {
								if (_this.active_card.replies === null) {
									_this.active_card.replies = [];
								}
								_this.active_card.replies.push(
									response.data.added_data[response.data.added_data_id]
								);
								_this.active_card.reply_text = '';
							}
							_this.active_card.replies_count++;
							_this.$bvModal.hide('comments_wrapper');
						} else {
							_this.error_message = response.data.message;
						}
					})
					.catch(function (error) {
						console.log(error);
					});
			}
		},

		scrollTo(elm_id) {
			console.log(elm_id);
			var E = document.getElementById(elm_id).scrollIntoView();
			console.log(E);
		},

		isUserNew() {
			console.log(this.$store.state.profile.reg_status);
			let _this = this;
			let reg_status = this.$store.state.profile.reg_status;

			if (reg_status === 'timezone_set' && reg_status !== null) {
				_this.$refs['tour'].show();
			}
		},

		tourClosed() {
			let _this = this;

			console.log('tour closed')

			let params = new URLSearchParams();
			params.append('token', _this.$store.state.session_id);
			params.append('reg_status', 'tour_finished');

			axios({
			url: _this.$store.state.api_url + "regStatusUpdate",
			params: params,
			})
			.then(function(response) {
				if (response.data.status == 1) {
				} else {
				console.log("Response error");
				}
			})
			.catch(function() {});
		},
	},
	mounted() {
		this.getUserDashboardCards();

		if (this.$store.state.profile.timezoneOffsetH === null) {
			this.$router.push('/profile');
		}
	},
	watch: {
		$route(to) {
			this.page_loaded = 0;
			this.section_title = to.meta.title;
			this.page_title = to.meta.title;
			this.getUserDashboardCards();
		},
	},
};
</script>

<style scoped>
.card-columns {
	column-count: 1;
}
@media screen and (min-width: 768px) {
	.card-columns {
		column-count: 2;
	}
}
@media screen and (min-width: 1050px) {
	.card-columns {
		column-count: 3;
	}
}

.card-header {
	padding: 5px 10px 5px 10px;
}
.card-body {
	padding: 0.25rem;
	padding-top: 0;
}
.card-footer {
	padding: 5px 10px 5px 10px;
}
.card-header .btn-sm {
	padding: 0px 5px 0px 5px;
	margin: 0;
}
.card_hightlight {
	border: 2px solid #d1af60;
}
#comments_wrapper {
	width: 100%;
	position: absolute;
}
</style>
